// دعوتِ کسی که حساب ندارد — فعلاً فقط از راهِ ایمیل (مایگریشن 0066).
//
// دو مخاطبِ کاملاً جدا، برای همین دو شیء:
//
//   inviteRepo         سازنده‌ی سیگنال. لاگین کرده، و RPC اجازه‌اش را چک
//                      می‌کند (فقط سیگنالِ خودش).
//   publicInviteRepo   کسی که ایمیل را گرفته. **حساب ندارد** و کلید anon
//                      دستش است؛ تنها دریچه‌اش همین دو تابعِ
//                      security definer است.
//
// قرینه‌ی `publicSignalRepo` در signalRepo.ts است، با یک فرقِ مهم: آن یکی
// لینکِ عمومیِ مشترک را می‌خواند و برای همین مجبور است اسم بپرسد؛ این یکی
// می‌داند کی پشتِ لینک است.
import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { normalizePhone } from '../lib/contacts';
import { phoneRepo } from './phoneRepo';
import type { ResponseKind, SignalId } from '../domain/types';
import type { RawSignalEvent } from '../domain/guestView';

/** یک نفر که با ایمیل دعوت می‌شود. اسم اختیاری است — فقط برای خطابِ ایمیل. */
export interface EmailInvitee {
  email: string;
  name?: string | null;
}

/** یک نفر که با پیامک دعوت می‌شود. */
export interface PhoneInvitee {
  phone: string;
  name?: string | null;
}

/** همان، به‌علاوه‌ی توکنی که سرور برایش ساخته. */
export interface PhoneInviteToken extends PhoneInvitee {
  token: string;
}

/** چیزی که صفحه‌ی دعوت نشان می‌دهد. شکلش را `get_signal_by_invite` می‌سازد. */
export interface InviteView {
  title: string;
  kind: string;
  note?: string | null;
  time_mode: string;
  starts_at?: string | null;
  ends_at?: string | null;
  date_only?: boolean;
  approx_label?: string | null;
  place_name?: string | null;
  cost_note?: string | null;
  /** از مایگریشن 0080 می‌تواند `cancelled` هم باشد — لینک زنده، برنامه لغو. */
  status: string;
  creator_name: string;
  invited_name?: string | null;
  my_response: ResponseKind;
  share_token: string;
  /** رخداد شروع شده — جواب دیگر عوض نمی‌شود. */
  locked: boolean;
  tz: string;
  /** رخدادِ کاتالوگی که سیگنال ازش زاده شده (مایگریشن 0081). */
  event?: RawSignalEvent | null;
}

/** جوابی که مهمان می‌تواند بدهد. `pending` و `conditional` از این‌جا در نمی‌آیند. */
export type GuestResponse = 'in' | 'out' | 'maybe';

export const inviteRepo = {
  /**
   * چند نفر را با ایمیل به یک سیگنال دعوت می‌کند.
   *
   * ایمیل‌های نامعتبر و تکراری بی‌صدا رد می‌شوند و توی `skipped` شمرده —
   * پرت کردنِ خطا برای یک آدرسِ غلط یعنی هیچ‌کدام از بقیه هم دعوت نمی‌شوند.
   */
  async byEmail(
    signalId: SignalId, invitees: EmailInvitee[],
  ): Promise<{ invited: number; skipped: number }> {
    if (!invitees.length) return { invited: 0, skipped: 0 };
    if (isDemoMode) return { invited: invitees.length, skipped: 0 };

    const { data, error } = await supabase.rpc('invite_by_email', {
      p_signal_id: signalId,
      p_invites: invitees.map((i) => ({ email: i.email, name: i.name ?? null })),
    });
    if (error) throw error;
    return data as { invited: number; skipped: number };
  },

  /**
   * برای هر شماره یک لینکِ شخصی می‌سازد و برمی‌گرداند.
   *
   * ⚠️ **هیچ پیامکی نمی‌فرستد.** فرستادن کارِ اپِ پیامکِ خودِ گوشیِ کاربر
   * است؛ این فقط توکن‌ها را می‌آورد تا متنِ پیام ساخته شود. برخلافِ ایمیل
   * که سرور می‌فرستدش.
   *
   * شماره‌ای که از قبل دعوت شده، توکنِ موجودش برمی‌گردد — چون ممکن است
   * کاربر پیامک را نفرستاده باشد و دوباره تلاش کند.
   */
  async bySms(signalId: SignalId, invitees: PhoneInvitee[]): Promise<PhoneInviteToken[]> {
    if (!invitees.length) return [];
    if (isDemoMode) {
      return invitees.map((i, n) => ({ ...i, token: `demo-invite-${n}` }));
    }
    // `invited_phone` همون‌جوری که بفرستیم ذخیره میشه — پس بین‌المللی می‌فرستیم
    // (WHOSUP-162). شماره‌ای که نرمال نمیشه عیناً می‌ره تا سرور خودش رد کنه.
    const cc = await phoneRepo.myCallingCode();
    const { data, error } = await supabase.rpc('invite_by_sms', {
      p_signal_id: signalId,
      p_invites: invitees.map((i) => ({
        phone: normalizePhone(i.phone, cc) ?? i.phone, name: i.name ?? null,
      })),
    });
    if (error) throw error;
    return (data ?? []) as PhoneInviteToken[];
  },

  /**
   * دعوت‌های یک سیگنال، برای **سازنده‌اش**.
   *
   * RLS تضمین می‌کند که فقط سازنده ردیف‌ها را می‌بیند (سیاستِ `si_sel` در
   * مایگریشن 0066) — این‌جا هیچ چکِ اضافه‌ای لازم نیست و اگر کسِ دیگری صداش
   * بزند فهرستِ خالی می‌گیرد.
   *
   * به چه درد می‌خورد: کاربر می‌خواهد لینکِ شخصیِ یک نفر را بردارد و از هر
   * راهی که خودش دوست دارد بفرستد — واتساپ، تلگرام، هر چیزی. بدونِ این،
   * لینکِ شخصی فقط از راهِ ایمیل یا پیامکِ خودمان بیرون می‌رفت.
   */
  async forSignal(signalId: SignalId): Promise<InviteRow[]> {
    if (isDemoMode) return [];
    const { data, error } = await supabase
      .from('signal_invites')
      .select('id, recipient_row_id, token, channel, destination, sent_at, error, opened_at')
      .eq('signal_id', signalId);
    if (error) throw error;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    return (data ?? []).map((r: any) => ({
      id: r.id,
      recipientRowId: r.recipient_row_id,
      token: r.token,
      channel: r.channel,
      destination: r.destination,
      sentAt: r.sent_at,
      error: r.error,
      openedAt: r.opened_at,
    }));
  },

  /**
   * تلاشِ دوباره برای ایمیلِ دعوتِ نرفته (WHOSUP-136). فقط ایمیل: پیامک را
   * خودِ گوشیِ کاربر می‌فرستد و «دوباره» همان کپی/باز کردنِ دوباره‌ی لینک است.
   */
  async resendEmail(inviteId: number): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('resend_invite_email', { p_invite_id: inviteId });
    if (error) throw error;
  },

  /**
   * چند بار لینکِ عمومیِ این سیگنال باز شده — شمارشِ خام، فقط برای سازنده
   * (RLS جدولِ signal_link_stats). نبودنِ ردیف یعنی صفر.
   */
  async linkViews(signalId: SignalId): Promise<number> {
    if (isDemoMode) return 0;
    const { data, error } = await supabase
      .from('signal_link_stats').select('views').eq('signal_id', signalId).maybeSingle();
    if (error) throw error;
    return data?.views ?? 0;
  },
};

/** یک ردیفِ دعوت، آن‌طور که سازنده می‌بیندش. */
export interface InviteRow {
  id: number;
  /** به `Recipient.id` وصل می‌شود. */
  recipientRowId: number;
  token: string;
  channel: 'email' | 'sms';
  destination: string;
  /** فقط برای ایمیل معنی دارد — پیامک را خودِ گوشیِ کاربر می‌فرستد. */
  sentAt: string | null;
  /** خطای آخرین تلاشِ فرستادن (mark_invite_sent) — null یعنی خطایی نبود. */
  error: string | null;
  openedAt: string | null;
}

export const publicInviteRepo = {
  async byToken(token: string): Promise<InviteView | null> {
    if (isDemoMode) return null;
    const { data, error } = await supabase.rpc('get_signal_by_invite', { p_token: token });
    if (error) throw error;
    return (data as InviteView | null) ?? null;
  },

  async respond(token: string, response: GuestResponse): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('respond_by_invite', {
      p_token: token,
      p_response: response,
    });
    if (error) throw error;
  },

  /**
   * آدرسِ همین دعوت را از فرستنده‌اش مسدود می‌کند. توکن هر دو طرف را بدون
   * لو دادنِ ایمیل در URL مشخص می‌کند؛ برای همین حساب کاربری لازم نیست.
   */
  async blockSender(token: string): Promise<boolean> {
    if (isDemoMode) return true;
    const { data, error } = await supabase.rpc('block_invite_sender', { p_token: token });
    if (error) throw error;
    return data === true;
  },
};
